// The client's entry (R431, R505; plan C.2 `fast/main.ts`). `index.html`
// loads this module.
//
// It is the boot of the client of before R505 with the renderer half taken
// out. The engine replaces the scene-graph application, the layer slot table
// replaces the stage tree, and the frame loop replaces the ticker.
// Everything that is NOT a renderer is imported and not copied: `state.ts`,
// `net/connection.ts`, `ui/overlay.ts`, `ui/settings.ts`, `ui/theme.ts`,
// `events.ts`, `world.ts` and every `gen/*`.
//
// Milestone M1 is the map: terrain, territory, the heatmap, the camera, the
// viewport report and the statistics line. M2 adds the objects, M3 the
// dynamics, M4 the rest of the chrome — sound, the clan room, the replay
// viewer and the display switches.
//
// The ARRIVAL has landed (R163, R182, R184, R499, R838): the map's own
// animation in its six styles, the chrome dock-in, the season banner that
// rides it, the chart's travel, the arrival's territory alpha and its zoom
// nudge. `ui/dockin.ts` and
// `ui/banner.ts` are imported here now, because both take `reducedMotion`
// from the engine-free leaf `render/motion.ts` rather than from a layer
// (`nopixi.test.ts` is what says so).
//
// The FLUFF has landed too (R101, R499): fog, marine snow and its ping-lit
// pass, the pressure haze, the whales, the beast of the old charts (R525)
// and the seaquake's tremor and silt. Each piece answers the OPTIONS `fluff` switch AND one
// knob of `fast/knobs.ts`, so the owner can price a piece live without a
// reload (R504). The seaquake layer is the one that is never hidden: an
// invisible layer is not advanced, and the tremor clock must not stop.
//
// The INTERACTION has landed (R456, R469, R492, R500, R501): the drawn
// crosshair of every armed pick mode, the MOVE and GATHER picks and their
// `v` and `g` keys, the five layer-view keys, the two hex-grid views, the
// zoom LOD gate, the Oracle broadcast pulse and the map author's marks. The
// layer view and the gate are written in the frame and not in the key
// handler, because a hello can replace what a layer holds.
//
// The MAP is no longer built here (R503). `fast/scene.ts` owns the fourteen
// layers both pages of this client draw, the per-hello site layer, the wire
// arms that move them and their per-frame feed; this file builds one scene
// and keeps the six layers the core page alone has — the heat map, the two
// hex views, the crosshair, the arrival and the seaquake — plus the chart,
// which is not an engine layer at all. Three hooks go the other way, because
// an owner change stales the chart, the hex views and the WAR tab, and the
// selection adopts what a state frame brought.
//
// This module writes no sentence of its own. Every word a reader sees comes
// from `gen/strings.ts`, and the console lines are for a developer.

// The boot error painter comes first, so a throw in any module below paints
// its message over the background (R177).
import { bootSettled } from '../ui/booterror';
// The error report handlers stand second, so a fault in the evaluation of
// any module below is reported (fault-reports.md §2, R1582).
import '../net/faultboot';
import { installPerf, publishReport, type PerfHarness } from '../perf/harness';
import { Camera } from '../render/camera';
import { AudioDirector } from '../audio/director';
import { SFX_PING, arrivalCue } from '../audio/triggers';
import { CueWatch } from '../audio/cues';
import { ClanChatConnection, GAME_CHAT_TAIL, clanChatUrl } from '../net/clanchat';
import { noteFaultContext } from '../net/faultreport';
import { Connection, type FatalKind } from '../net/connection';
import { coverIfMaintenance } from '../ui/maintenance';
import { EventBus } from '../events';
import { GameState } from '../state';
import { ChromeDockIn } from '../ui/dockin';
import { SeasonBanner } from '../ui/banner';
import { mountImpBar } from '../ui/impbar';
import { type SmallMark, clearSmallMark, newSmallMark, offerSmallMark } from './smallmarks';
import { denAt } from '../dens';
import { laneOfMark } from '../ui/cards/lanemark';
import { workPress } from '../ui/cards/treasure';
import { poiHasCard } from '../ui/cards/mapcards';
import { workFit } from '../ui/cards/orders';
import { installViewportGuard } from '../ui/viewport';
import { Overlay } from '../ui/overlay';
import { namePress } from '../ui/seamountname';
import { gunRange } from '../ui/format';
import { rearArcDeg } from '../render/reararc';
import { slowRingPx } from '../render/detonation-recipe-kit';
import { ReplayFilm } from './film';
import { chartOverlay, sceneLookOf, startLayerView } from '../ui/look';
import { setDepthPick } from '../depthlook';
import { chromeLookOf } from '../ui/chromelook';
import { currentLook, soundSettings, wearTheme } from '../ui/themestate';
import { clusterBox, isSheet, onCluster } from '../ui/layout';
import { storeOf } from '../ui/localstore';
import { readCornerGrid } from '../ui/cornergrid';
import type { SpawnKind } from '../render/spawn-plan';
import type { CrosshairMode } from '../render/crosshair-paths';
import {
  advisorSetHere, applyRenderScale, firstSessionAdvisor, loadSettings, saveSettings,
} from '../ui/settings';
import { ADVISOR_KEY } from '../ui/advisor';
import { isFirstSession } from '../ui/firstsession';
import { GameHost } from '../ui/options/gamehost';
import { enableUiScale, uiZoom } from '../ui/uiscale';
import { MarkerMenu } from '../ui/markermenu';
import { audienceWire, kindWire } from '../render/map-markers';
import { CHART_FELL_FAMILY, MARKER_INKS } from './gfx/atlas';
import { MarkerInput } from './markers';
import { setAudioChoices, setSettingsSink } from '../ui/audiochoices';
import { BLOCK_RIDGE, WORLD, WORLD_H, cellAt, cellCenterX, cellCenterY, factionColor, mpxToPx } from '../world';
import { fast as S } from '../gen/strings';

import {
  getMe, getMyAchievements, getWardroom, join as apiJoin, messageOf, sendPlayerReport, type Join,
} from '../net/api';
import { createEngine, type Engine } from './engine';
import { framePeriod, UNCAPPED_FPS, type StatsSink } from './frame';
import type { FrameInfo, ViewRect } from './layer';
import { hubAddress, makeActions } from './actions';
import { Feed } from './feed';
import { MapScene, type SceneVisibility } from './scene';
import type { ChartHost } from './chart/furniture';
import { markMessageId } from './chart/pieces/battle-marks';
import { pinMessageId } from './chart/pieces/inbox-pins';
import { commissionOfMark } from './commission-pick';
import { needsFolded, needsLit } from '../ui/inbox/needs';
import {
  NoEntry, PAGE_PARAMS, fromJoin, resolveEntry, showStop, type Entry,
} from './entry';
import { CrosshairLayer } from './layers/crosshair';
import { HeatmapLayer } from './layers/heatmap';
import { HoverCellLayer } from './layers/hovercell';
import { HexBorderLayer, HexFillLayer } from './layers/hexgrid';
import { SpawnLayer } from './layers/spawn';
import { QuakeLayer } from './layers/quake';
import {
  CAMERA_DETAIL_MIN_ZOOM, CHROME_REPLAY_LINK_WAIT_MS, FLEET_COURSE, FLEET_ORDER_DRAG,
} from '../gen/presentation';
import { LaneOfferDesk, LaneWaters } from '../laneoffer';
import type { DragLane } from './layers/orderdrag';
import {
  bookAddress, forgetReplayLink, readReaderAddress, readReplayLink, type FilmMiss,
} from '../ui/hub/chronicle/address';
import { showReader } from '../ui/hub/chronicle/reader';
import { armReplayLink } from './replaylink';
import { armCellLink, forgetCellLink, listenPushOpen, readCellLink } from '../push/link';
import { Minimap, type MinimapSite } from './minimap';
import { Commands } from './commands';
import { Tooltip } from './tooltip';
import { installInput } from './input';
import { quickCtxOf } from './quickctx';
import { ChatChannel, Mission, QuickRefKind } from '../gen/proto/aquaphobia';
import { QuickWheel, installQuickPress, installViewHold, type QuickChannel } from '../ui/quickwheel';
import { posLookupOf, quickLineOf } from '../ui/quickchat';
import { Buzzer, browserBuzzEnv } from '../ui/buzz';
import { installTabIcon } from '../ui/tabicon';
import { startKeepAlive } from '../net/keepalive';
import { startTouchLog, touchLogSink } from './touchlog';
import { watchGeometry } from './touchgeom';
import { newContextMission } from './dragorder';
import { OrderDragLayer } from './layers/orderdrag';
import { PlotLayer } from './layers/plot';
import { MapHelm, turnRadS, type HelmFleet } from './maphelm';
import { onKnobs, type Knobs } from './knobs';
import { packRound } from './layers/fx';
import { UnlockQueue, readUnlockKnob } from '../ui/unlock';
import { readAtKnob, rememberSeaSocket, seaIdleHref } from './sealink';
import { CoveredArrival, SeaIdle, bindSeaIdle, isTyping, mountSeaFrame, seaIdleAllowed } from '../ui/seaidle';
import type { Cover } from '../ui/overlay/types';
import { hiddenDocument, reducedMotion } from '../render/motion';
import { RANK } from '../ui/firsthold';
import { askRecord, heldRecord, recordFailed, resetRecord } from '../ui/achievements';
import { SeasonSummaryCard } from '../ui/seasonsummary';
import { GuestWarnCard, guestFacts, rollDeletes } from '../ui/guestwarn';
import { SettlementBanner } from '../ui/settlement';
import { assignWithGesture, reloadOnReturn } from '../ui/nav';
import { ChromeVeil } from '../ui/seasonover';
import { PhotoMode } from '../ui/photomode';
import { browserOf, otherPlayersChat, ReportSheet } from '../ui/reportsheet';
import { clientLog } from '../net/clientlog';
import { ReportOutbox } from '../net/reportoutbox';
import { pageBuild, pageOf } from '../net/faultreport';
import { copyText } from '../ui/share';
import { captureMap, pngOf, reportShot } from './capture';
import { seasonDay } from './chart/pieces/shared';
import { setBackstorySource } from '../ui/backstory';
import { backendLabel, makeStatsSink } from './stats';
import { markFactor } from '../arcade/markscale';
import { bootPlatform } from '../platform/boot';

/**
 * The frame-time harness (plan C.7). `?perf=1` builds it, drives the camera
 * over the scripted paths and prints one report; without the parameter the
 * client builds nothing and every call site is an optional call.
 * `?uncap=1` lifts the 60 fps cap for the headroom run.
 */
const PERF_ON = PAGE_PARAMS.get('perf') === '1';
const PERF_UNCAP = PAGE_PARAMS.get('uncap') === '1';

/** How long the viewport report waits after the camera stopped (§10.3). */
const VIEWPORT_DEBOUNCE_MS = 250;

/** How long `?perf=1` lets the world settle before it runs the matrix. */
const PERF_SETTLE_MS = 5000;

async function boot(): Promise<void> {
  // The store app decides at boot, before the page draws (mobile-apps.md §4).
  bootPlatform();
  // `index.html` paints the `#blackout` cover before any script runs, so the
  // arrival starts on a black screen (R182). `ChromeDockIn` owns the element
  // from the line that builds it, a few statements down; until then a path
  // that never reaches that line has to lift the cover itself, because a
  // black page with no way out is the one thing the dock-in must never
  // leave behind.
  const blackout = document.getElementById('blackout');
  const liftCover = (): void => {
    if (!blackout) return;
    blackout.style.opacity = '0';
    blackout.style.display = 'none';
  };

  // The touch log, when `?touchlog=1` or the stored flag asks for it
  // (R1497). The hub and the world are this one page, so the log runs
  // through the menu and into the world, and it must stand before the
  // input installs.
  startTouchLog();
  // The live tab icon (R1653, brand.md §10), before the entry resolves:
  // the hub on this page feeds its INBOX count, and the world below feeds
  // its own.
  const tabIcon = installTabIcon();
  // The keep-alive (R1706): the HTTP/3 flow to this origin never stays
  // idle past a home router's UDP timeout while the page is visible.
  startKeepAlive();

  // The chrome's banner (R243), before anything is painted. No faction is
  // known yet, so AUTO resolves to what `styles.css` already declares,
  // and the stored ink of the next first paint stays (R1953).
  wearTheme(null);
  // The UI scale (R1556), before the first chrome root stands: the
  // landing, the hub, the reader and the shell of this page all wear it.
  // The account's copy, when the entry pulls it, applies through the store.
  enableUiScale(loadSettings().uiScale);
  // The render scale (R484 item 2), before the engine sizes its surface.
  applyRenderScale(loadSettings().renderScale);

  // The public reader of a shared chronicle volume (chronicle.md §9.4,
  // R1181). It is the whole page: no session, no hub, no world, no sound.
  const book = readReaderAddress(PAGE_PARAMS);
  if (book) {
    liftCover();
    showReader(book);
    bootSettled();
    return;
  }

  // The replay link of the chronicle book (chronicle.md §8, R1181). It is
  // read now and taken off the address at once, so a reload does not ask
  // for the film again and a join into another world does not carry it.
  const replayLink = readReplayLink(PAGE_PARAMS);
  forgetReplayLink();
  // The cell of a push notification (player-reach.md §3.5, R1301). It is
  // read now and taken off the address at once for the same reason.
  const cellLink = readCellLink(PAGE_PARAMS);
  forgetCellLink();

  // Sound is built first, before the entry is resolved, because the unlock
  // has to be armed before the player's first press (audio.md §3.6, R227).
  // The stored switches are applied before anything can make a sound, so a
  // player who turned the music off hears none of it here either. The
  // manifest read is fire-and-forget: a host that published nothing leaves
  // the director unavailable and every cue below is a no-op.
  const audio = new AudioDirector();
  audio.applySettings(soundSettings(loadSettings()));
  // Menu mode goes on BEFORE the manifest read (R396). `init` judges the
  // handoff against the playlist this page selects on its own, and every
  // way into this page opens on a menu — so the menu playlist must already
  // be the selection when the manifest lands, or a stored in-game playlist
  // would drop the record the arcade page wrote and the music would
  // restart. The entry paths that skip the menu release the mode below.
  audio.menuMusic(true);
  const manifestReady = audio.init();
  audio.unlock();

  // The hub's SETTINGS panel writes the same settings blob the in-game ☸
  // OPTIONS card writes, and a change there must be heard at once. The
  // director is built here and the hub is handed no reference to it, so the
  // panel is fed through two hooks (R229): one sink that applies a written
  // blob, and one push of what the manifest actually published. Without the
  // second one the two pickers say that this host serves no sound, which is
  // also the final answer on a host that published nothing.
  setSettingsSink((s) => audio.applySettings(soundSettings(s)));
  void manifestReady.then(() => setAudioChoices({
    available: audio.available,
    playlists: audio.playlists(),
    themes: audio.themes(),
  }));

  // The impersonation strip's root, before the entry resolves. The
  // landing screen and the hub each take their own root off the body when
  // they resolve, and `#ui` does not exist yet, so the strip is a fourth
  // root on the body itself. `resolveEntry` raises it as soon as it knows
  // whose session this is, which is while the first screen is still up.
  mountImpBar();
  // A rotation on iOS can leave the window scrolled, which moves every
  // tap off its control (R1277).
  installViewportGuard();

  // The way in comes next: the landing screen must be up before the canvas
  // exists, and nothing below should build a renderer for a player who is
  // still choosing a world (R502).
  let entry: Entry;
  try {
    entry = await resolveEntry(audio);
  } catch (err) {
    // A hub or a landing screen that rejects leaves no world to draw; the
    // cover must not stay up over the stop notice.
    liftCover();
    showStop(err instanceof NoEntry ? err.message : String(err));
    return;
  }
  // A world comes next, so the cover of a menu entry turns black for the
  // arrival (`public/entrycover.js`, R1773).
  document.documentElement.classList.remove('menu-entry');
  // A meta session has a meta server behind the origin: the clan room, the
  // wardroom, the achievement record, the re-join and the two links all
  // ask it. A `?server=` lane has none, and each of them stands down.
  const metaMode = entry.mode === 'meta';
  // The replay link acts only in the world it names. A deep link the entry
  // could not honour lands in the hub, and the world the hub then joins is
  // another one, where the reference names nothing. A link that acts plays
  // its film and nothing else (chronicle.md §8, R1684): the world stays
  // under the black, no season banner rises, and every way out goes back
  // to the chronicle book.
  const filmLink = replayLink && metaMode && replayLink.world === entry.worldId
    ? replayLink : undefined;
  // The entry is resolved, so whatever comes next is a world. The hub and
  // the landing release the mode themselves when they close on a join;
  // this call is for the paths that never showed them — the deep link and
  // the direct connect — and it is idempotent over theirs (R396).
  audio.menuMusic(false);
  // The account may have handed this browser another blob while the menu
  // was up (R256). The director was fed the local copy before the screen
  // opened, so it is told again here. The call is idempotent, and it costs
  // nothing where the two copies agree.
  //
  // The look is resolved ONE time here, from the picks, the entitlement set
  // the entry just read and the cached custom bodies (themes.md §12, R814).
  // A player cannot change a theme during a match (R807), so every reader
  // below takes it from this object and none reads a pick again.
  const look = currentLook();
  audio.applySettings(soundSettings(loadSettings(), look));

  const canvas = document.createElement('canvas');
  // The map fills the window. The page's own rules give it `display:block`
  // and `touch-action:none`; the box is stated here, because the canvas is
  // made here and nothing else in the document has a size.
  canvas.style.cssText = 'position:fixed;left:0;top:0;width:100%;height:100%;';
  document.body.appendChild(canvas);

  // The statistics sink and the perf harness are wired after the socket and
  // the overlay exist, so the engine is handed two indirections it may call
  // from its very first frame.
  let statsSink: StatsSink | null = null;
  let perf: PerfHarness | undefined;
  let onFrame: ((frame: FrameInfo) => void) | null = null;
  let chromeFrame: (() => void) | null = null;

  let engine: Engine;
  try {
    engine = await createEngine(canvas, {
      maxFps: PERF_UNCAP ? UNCAPPED_FPS : undefined,
      onStats: (line, stats) => statsSink?.(line, stats),
      onPhase: (phase) => {
        if (!perf) return;
        if (phase === 'start') perf.frameStart();
        else if (phase === 'end') perf.frameEnd();
        else perf.phase(phase);
      },
      onFrame: (frame) => onFrame?.(frame),
      // A paused map runs the chrome's share of the frame on its idle
      // sweep (R1755).
      onIdle: () => chromeFrame?.(),
    });
  } catch (err) {
    console.error(err);
    liftCover();
    showStop(S.no_webgl2);
    return;
  }
  // The canvas is in the document and the engine stands: a later fault goes
  // to the console only, and never covers the map.
  bootSettled();
  touchLogSink()?.(`backend ${backendLabel(engine.gfx)}`);
  // The geometry lines (R1498): nothing while the log is off.
  watchGeometry(engine.gfx);

  // The camera is the shipping client's, on a plain object rather than on a
  // PixiJS container (plan C.1). The shake rides its own member, so a
  // tremor never reaches the pan clamp or the viewport report.
  const camera = new Camera(engine.view, canvas, engine.view.shake);
  // `onGrid` is the scene's, and the scene is built below.

  // -- the map layers ------------------------------------------------------

  const state = new GameState();

  /**
   * A cue that belongs to a place on the map — a blast, a sonar sweep.
   *
   * The director dampens it by distance from the ear and by zoom, and the
   * ear is where `onFrame` last said the view was.
   */
  const cue = (id: string, x: number, y: number, scale?: number,
    voice?: boolean): void => {
    audio.playAt(id, x, y, scale, voice);
  };

  /**
   * The cue watcher (R547). It is built with the event bus below, because it
   * raises client-local events, and the bus needs the chrome the scene is
   * built before. The scene and the frame loop reach it through this slot.
   */
  let watch: CueWatch | null = null;
  /** One reused point: the view centre the beacon pulse is measured from. */
  const ear = { x: 0, y: 0 };

  // The chart is a picture, so it lives on a canvas — its own small one,
  // between the map and the chrome (the plan's C.5).
  const minimap = new Minimap();

  /** One reused world point: the aim the chart furniture draws to (R548). */
  const chartAim = { x: 0, y: 0 };
  /**
   * What the chart furniture reads of this page (R548).
   *
   * Every member is read late, because the chrome, the tooltip and the
   * command module are all built after the scene. The furniture touches
   * none of them before the first frame.
   */
  const chartHost: ChartHost = {
    get root(): HTMLElement { return ui.furnitureEl; },
    hover: () => tooltip.lastHit,
    hoverAt: () => tooltip.hover,
    hoverCell: () => tooltip.hoverCell,
    // The peek names a point first; a selected point names it after
    // (R3138), so a selected gate keeps its area.
    peekPoi: () => tooltip.peekPoi || (state.selection?.kind === 'point' ? state.selection.id : 0),
    // The armed aim, in WORLD px: the torpedo-run piece draws the fuze
    // circle where the round would go off, and that is a place on the map.
    aim: () => {
      const a = commands.aimPoint;
      return a ? toWorld(a.cx, a.cy, chartAim) : null;
    },
    onCluster: (fn) => onCluster(fn),
    // The corner grid the graticule writes on (R1812, round 086 card 14).
    edges: () => readCornerGrid(isSheet()),
    // The inbox pins stand while NEEDS YOU stands open in the INBOX, and
    // the pointed, focused or last pressed item lights its pin (R1846).
    needsPins: () => ui.inboxShown && !needsFolded(),
    needsLit: () => needsLit(ui.inboxShown),
    // Card 28 (R2585): the radius of the harbour limits while the flag
    // `modifiers` holds. The control reads the flag; the piece draws.
    harbourPx: () => (state.hasFeatureFlag('modifiers') ? state.rules.harbourLimitsPx : 0),
    // Card 50 (R2637): the narrows a pirate group hunts at, while the flag
    // `pirates` holds. The control reads the flag; the piece draws.
    npcWater: () => state.hasFeatureFlag('pirates'),
    cachesOn: () => state.hasFeatureFlag('treasure'),
  };

  /** An owner change stales the chart, the two hex views and the WAR tab. */
  const ownersMoved = (): void => {
    minimap.invalidate();
    hexFills.invalidate();
    hexBorders.invalidate();
    ui.warEvent();
  };

  // The map itself (R503): the fourteen layers both pages of this client
  // draw, the per-hello site layer, the wire arms that move them and the
  // per-frame feed they need. The three hooks are what the core page adds
  // around a state frame — the chart, the two hex views and the WAR tab
  // stale on an owner change, and the selection adopts what arrived.
  const scene = new MapScene(engine, {
    state,
    camera,
    cue,
    torpedoStyle: look.world.torpedo,
    // The chart furniture (R548). The season map draws it; the reading is
    // the reader's own pick, written every frame below.
    chartHost,
    listener: (view, zoom, x, w) => {
      audio.setListener(view, zoom, x, w);
      // The one per-frame reader of the watcher: the anchored Beacon
      // nearest the view centre pulses on its own clock (R547). Two
      // divisions and one compare on a frame that owes no pulse.
      ear.x = (view.x0 + view.x1) / 2;
      ear.y = (view.y0 + view.y1) / 2;
      watch?.onFrame(ear, performance.now() / 1000);
    },
    // One flag a flip, and the rebuild waits for the first VISIBLE frame: a
    // whole season of flips on a hidden view costs these two writes. A flip
    // is also the event that stales the WAR data — the overlay re-queries
    // only while the tab is on screen, debounced (R83).
    onOwnersReplaced: ownersMoved,
    onFlip: ownersMoved,
    onFleetsApplied: () => { commands.adoptNewFleets(); },
  });
  // The canvas half of the look (themes.md §3.4, §3.5, R814). It is written
  // ONE time, because the look is resolved one time a session, and the
  // scene passes it on to the layer a hello builds.
  const sceneLook = sceneLookOf(look);
  scene.setLook(sceneLook);
  // The depth look of the theme (R2726): the legend of the VIEW panel and
  // the route profile read it, flipped by the key `\` as the map is.
  setDepthPick(sceneLook.depth);
  // The scene's own layers, named here so every reader below — the tooltip,
  // the decisions, the overlay's read-backs, the perf harness and the debug
  // handle — reads the one it always read.
  const {
    terrain, territory, control, sonar, wake, fleets, torpedoes, fx,
    markers, fog, snow, haze, creatures, kraken,
  } = scene;

  // The six layers the core page alone has. The slot order is the engine's
  // (the plan's A.5); the calls below only say which layer stands where.
  const heatmap = new HeatmapLayer(engine);
  // The dashed outline of the hovered cell while a peek stands (R1875).
  const hoverCell = new HoverCellLayer(engine);
  // The two hex-grid views (R456, R501). Both ship HIDDEN: they are a
  // reader's tool and not the map, and an invisible layer is neither
  // advanced nor drawn, so a season of flips on them costs one flag each.
  const hexFills = new HexFillLayer(engine);
  const hexBorders = new HexBorderLayer(engine);
  hexFills.setVisible(false);
  hexBorders.setVisible(false);
  // Both write the hex cell's UVs on a rebuild only, so an atlas grow stales
  // them. The engine lives as long as the page, so nothing unsubscribes.
  engine.atlas.onRepacked(() => { hexFills.invalidate(); hexBorders.invalidate(); });
  // The mark that names an armed pick mode (R469, R500). It draws over the
  // map and under the arrival, so a mode armed during the overture does not
  // draw through it.
  const crosshair = new CrosshairLayer(engine);
  // The drag order preview (R1208): the order a finger that drags an own
  // fleet will give, drawn while the finger travels.
  const orderDrag = new OrderDragLayer(engine);
  // The draft course of the plot line (R2589): the legs the commander
  // plots, while the plot line stands.
  const plotLayer = new PlotLayer(engine);
  // The arrival's own slot, last of the table so it covers everything
  // (R163). It plays for three seconds a session and costs one call that
  // returns at once the rest of the time.
  const spawn = new SpawnLayer(engine, engine);
  // The seaquake (R101). It is the core page's, not the scene's: an arcade
  // match has no seaquake at all, and this layer is the one that is never
  // hidden, because an invisible layer is not advanced and the tremor clock
  // must not stop.
  const quake = new QuakeLayer(engine);
  // Every layer carries the name of the slot it fills, as the scene's do.
  for (const l of [heatmap, hoverCell, hexFills, hexBorders, orderDrag, plotLayer, crosshair, spawn, quake]) {
    engine.setLayer(l.name, l);
  }

  // The knobs, read now and again on every `hashchange` (R431, R493). The
  // scene answers for the pieces it owns — the two ambience layers, the
  // haze, the creatures and the wake cap; the seaquake is this page's.
  /** The preview knob plays once a session, not once a season roll. */
  let unlockKnobPlayed = false;
  /** The `#at` knob acts once a page, at the first arrival (R2492). */
  let atKnobRead = false;
  // `onKnobs` calls back at once, so the hash is parsed one time at boot.
  let knobs!: Knobs;
  onKnobs((k) => {
    knobs = k;
    scene.applyKnobs(k);
    quake.configure(k.silt === 1, k.tremor === 1, k.siltn);
  });

  // -- the state, the chrome and the wire ----------------------------------

  // The event bus turns a happening into a toast and a sound. The table it
  // reads comes from the server in the hello catalog, so the overlay and the
  // director stay unaware of each other — the bus is the only thing that
  // holds both (ui-map-interface.md §15 "The toast", audio.md §8.5).
  const bus = new EventBus({
    toast: (text, o) => ui.notice(text, o),
    audio,
    // The club share's cue waits for the day cue (R3134).
    later: (fn, ms) => { setTimeout(fn, ms); },
  });

  // The frames say more than the notices do (R547): a reload that finished,
  // a hull a hostile now hears, a deposit that ran dry, a captain who
  // reached the peak of a career. The watcher reads those edges and raises
  // the client-local event whose row plays the cue.
  watch = new CueWatch({
    local: (id) => bus.local(id),
    cue,
    nowSec: () => performance.now() / 1000,
    sites: () => state.sites,
    // The two clock rules of R550. Both figures are extrapolated on the
    // world's own clock, so the watcher asks the cache and holds neither.
    worldClockSec: () => state.worldClockSec(),
    truceLeftSec: () => state.truceLeftSec(),
  });

  // The sweep sounds from the base that pings, damped like any map cue.
  sonar.onPing = (x, y) => cue(SFX_PING, x, y);

  /**
   * Hands the overlay the audio picture it draws: what the host published,
   * and where the transport stands. The overlay never reads the director.
   */
  const pushAudio = (): void => {
    ui.setAudio({
      available: audio.available,
      playlists: audio.playlists(),
      themes: audio.themes(),
      status: audio.musicStatus(),
    });
  };

  /**
   * The clan's room (R222, R225), or null in direct mode.
   *
   * A `?server=` lane is a game server with no meta server behind the
   * origin, so the socket has nothing to reach. The CLAN tab then says what
   * is missing rather than showing an empty box that never fills.
   */
  let clanChat: ClanChatConnection | null = null;

  let conn!: Connection;
  let currentWorld = entry.worldId;
  noteFaultContext({ world: currentWorld });
  // The word this world's mode carries (R951). A handover can land this
  // client in another world, so it moves with `currentWorld`.
  let currentWorldMode = entry.worldMode;
  // The map's decisions and the map's words. Both are built below, after
  // the feed whose world they read, so everything that reaches them early
  // does so through a closure.
  let commands!: Commands;
  let tooltip!: Tooltip;

  // The world goes down before the page leaves it (R1203): MENU and the
  // guest warning's REGISTER both leave through it.
  /** True once the page left its world: the back-forward cache must not show a dead map. */
  let leftWorld = false;
  // A return from the back-forward cache after a leave reloads (R2817, ui-map-interface.md §16.12).
  reloadOnReturn(() => leftWorld);
  const leaveWorld = (): void => {
    leftWorld = true;
    engine.stop();
    conn?.close();
    clanChat?.close();
  };
  /** Takes the world down and leaves for `href`. A teardown that throws is
   *  logged under `tag` and never keeps the player on the page. */
  const leaveWorldFor = (tag: string, href: string, go?: (u: string) => void): void => {
    try { leaveWorld(); } catch (err) { console.warn(`[${tag}] teardown`, err); }
    assignWithGesture(href, go);
  };

  /** Leaves the world for the chronicle book, on the live volume of
   *  `world` (R1684). `miss` is why no film played, for the hub's notice.
   *  The address replaces this one, so BACK in the book does not join the
   *  world again. */
  const backToBook = (world: number, miss?: FilmMiss): void => {
    leaveWorldFor('film', bookAddress(hubAddress(location.href, 'record'), world, miss),
      (u) => location.replace(u));
  };

  // The idle sea view of the map (R2865, ui-map-interface.md §13 "SEA
  // VIEW"): after the idle time with no input, the map blends into the sea
  // view in a frame over the live map. The frame dials the socket of this
  // world, as SEA VIEW HERE does (R2494), and the map stays in the world.
  // It pauses the map under its frame, and a cover change must not take
  // that pause away: the cover is kept, and the frame period goes back to
  // it when the map shows again.
  let cover: Cover = 'none';
  let seaPaused = false;
  // An arrival that a hello starts under the frame holds with the paused
  // loop; the return skips it with no cue and shows the map as it stands
  // (R2882).
  const coveredArrival = new CoveredArrival();
  const seaIdle = new SeaIdle({
    now: () => performance.now(),
    allowed: () => seaIdleAllowed({
      setting: ui.settings.seaIdle,
      gpu: typeof navigator !== 'undefined' && 'gpu' in navigator,
      reducedMotion: reducedMotion(),
      world: state.hello !== null,
      typing: isTyping(document),
    }),
    hidden: hiddenDocument,
    idleMin: () => ui.settings.seaIdleMin,
    href: () => {
      const h = state.hello;
      if (!h) return null;
      rememberSeaSocket(storeOf('session'), h.worldId, conn.url);
      return seaIdleHref(location.href, h.worldId, camera.centre(), currentWorldMode ?? '');
    },
    mount: (href) => mountSeaFrame(href),
    pauseMap: (on) => {
      seaPaused = on;
      engine.loop.setPeriod(on ? null : framePeriod(cover, PERF_UNCAP));
      coveredArrival.cover(on, () => spawn.skip());
    },
    // The map is quiet under the frame, and its music plays on (R2977).
    hushMap: (on) => audio.hushSfx(on),
    centre: (x, y) => camera.centerOn(x, y),
    changed: () => ui.render(true),
  });
  // The window listeners go on before the overlay's own, so a key that
  // ends the tour reaches no handler of the chrome (`onCardKey` listens in
  // the capture phase too). Every hook reads the overlay lazily: the first
  // tick comes a second after this line.
  bindSeaIdle(seaIdle);

  const actions = makeActions({
    conn: () => conn,
    ui: () => ui,
    state,
    camera,
    bus,
    heatmap,
    audio,
    pushAudio,
    clanChat: () => clanChat,
    commands: () => commands,
    // MENU takes the world down before the page leaves (R1203).
    leave: leaveWorld,
    fleetAt: (id) => fleets.at(id),
    worldMode: () => currentWorldMode,
  });

  const ui = new Overlay(state, actions);
  ui.seaIdleCountdown = () => seaIdle.countdown();
  // The chrome half of the look (themes.md §3.4, R814): `#ui` wears one
  // class per chrome field, and the notices column takes its own id. It is
  // written ONE time, because the look is resolved one time a session.
  ui.setChromeLook(chromeLookOf(look));
  // The theme decides whether the faction key exists (R1747); the
  // LEGEND tile of a telephone stands for the key or a deep field (R2832).
  ui.factionKeyThemed = sceneLook.factionKey === 'key';
  // The cover state sets the frame period of the map (R1755, §10.5): a
  // pause under the opaque telephone menu, a cap under the full sheet.
  // While the idle sea view covers the map, the map stays paused, and the
  // cover waits for the return.
  ui.onCover = (c) => {
    cover = c;
    if (!seaPaused) engine.loop.setPeriod(framePeriod(c, PERF_UNCAP));
  };
  // The chrome learns what the host published only when the manifest lands.
  // The read started at the top of `boot`; this picks up the answer, and
  // `ui` exists by now to take it.
  void manifestReady.then(() => { pushAudio(); ui.render(true); });

  // The chrome dock-in (R182): the screen is black until the arrival starts,
  // and the panels travel in as it closes. It is built here, after the
  // overlay has put `#ui` in the document, and it covers the screen straight
  // away — from this line to the first hello nothing but black is on glass.
  const dockin = new ChromeDockIn(
    ui.shellEl,
    blackout,
    look.world.dock,
  );
  dockin.cover();
  // The chrome behind the season summary (R1519, ui-map-interface.md §3.4):
  // while a summary stands, the map and the card are the whole screen. On
  // a page load the chrome has not arrived, so the veil hides it at once;
  // in a running session it fades out. The minimap wears the veil too.
  const veil = new ChromeVeil({
    shell: ui.shellEl,
    state,
    arriving: () => dockin.state !== 'idle',
    also: () => [document.getElementById('fast-minimap')],
  });
  ui.chromeVeiled = () => veil.active;
  // The season timelapse of WAR · SOVEREIGNTY (card 09 of round 076,
  // R1620). A frame takes the traced outline and the minimap; null gives
  // both the live owners back.
  ui.warTl.setMap({
    show: (owners) => {
      territory.showFrame(owners);
      minimap.setOwners(owners ?? scene.owners);
    },
  });
  // The season banner (R184): the title card rises on the frame the chrome
  // starts its travel, so the words and the panels arrive together.
  const banner = new SeasonBanner(ui.bannerEl, look.world.banner);
  /** Which arrival the chrome and the card are playing. The hello sets it. */
  let arrivalKind: SpawnKind = 'login';
  // The title card the welcome card held rises first in the release, but
  // not over an open telephone sheet: SEA TRIALS opened it for its rows,
  // and the card's kicker named the season and the day (R2138).
  const heldBanner = (): void => { if (!ui.sheetStandsOpen) raiseBanner(); };
  const raiseBanner = (): void => {
    // The welcome card is the first frame (R2127): the title card waits for
    // its close and rises first in the release. The key keeps one.
    if (ui.firstHold.defer('banner', RANK.title, heldBanner)) return;
    const h = state.hello;
    banner.raise({
      kind: arrivalKind,
      worldName: h?.worldName ?? '',
      region: h?.region ?? '',
      season: h?.season ?? 1,
      leftSec: state.seasonLeftSec(),
      seasonSec: state.rules.seasonSec,
    });
  };
  // The chrome arrives inside the map's own arrival (R163), `LATE_LEAD_SEC`
  // before its end: most of the map is revealed by then, and the panels land
  // as it closes. `onEnd` stays wired for the one path the lead never
  // reaches — a skip before the late point — and `dock()` ignores the second
  // call on every other path. `raise()` ignores it the same way.
  //
  // Behind a season summary the chrome does not arrive (R1519): the
  // dock-in settles with no travel and the banner stays down.
  // The arrival's own cue (R598, R1533). It sounds when the wave starts and
  // not on the press in the hub, nor while the veil holds: the black screen
  // and the still veil before the wave are not the arrival. A resync
  // answers null and stays silent.
  spawn.onPlay = () => {
    // The arrival a hello started under the idle sea view is silent (R2882).
    if (coveredArrival.quiet) return;
    const sfx = arrivalCue(arrivalKind);
    if (sfx) audio.play(sfx);
  };
  spawn.onLate = spawn.onEnd = () => {
    if (veil.active) { dockin.abort(); return; }
    dockin.dock();
    raiseBanner();
  };

  /** The home hold in CSS px. One object, reused: the arrival reads it on
   *  every frame of its run, and a frame must allocate nothing (R131). */
  const spawnHome = { x: 0, y: 0 };

  // The hub cards aboard ☸ OPTIONS and the standing orders (R1731). Only a
  // page with a meta server holds an account; a direct page draws one line
  // in those sections. The BUZZ switches stay in the overlay's own blob,
  // which the buzzer reads (R1613).
  if (metaMode) {
    ui.optionsHost = new GameHost({
      getMe,
      refresh: () => ui.render(true),
      wide: () => !isSheet(),
      go: (href) => actions.leaveFor(href),
      buzz: {
        off: () => ui.settings.buzzOff,
        setOff: (off) => { ui.settings.buzzOff = off; saveSettings(ui.settings); },
      },
      // The PIRATES row of ALERTS stands while the world latches the flag
      // (R2637).
      hasFlag: (flag) => state.hasFeatureFlag(flag),
    });
  }

  // Dial the clan's room (R222, R225). Only meta mode dials.
  if (metaMode) {
    clanChat = new ClanChatConnection({
      onWelcome: (w) => state.clanChatWelcome(w.clanId, w.self),
      onEntry: (e) => state.addClanChat(e),
      // A refused frame is the sender's business and nobody else's: the
      // socket stays open, so a notice is the whole of the answer.
      onError: (_code, message) => ui.notice(message, true),
      onRelay: (on) => state.setClanChatRelay(on),
      onStatus: (on) => state.setClanChatConnected(on),
      onStop: (kind) => state.setClanChatStop(kind),
    }, clanChatUrl(GAME_CHAT_TAIL));
  } else {
    state.setClanChatStop('unavailable');
  }

  // The captain card's backstory (R1246): only a meta session has a
  // wardroom to read, and the read names the mode of the world this client
  // plays in NOW, so a handover to another world reads again.
  const readBackstories = (): void => {
    setBackstorySource(metaMode && currentWorldMode
      ? () => getWardroom(currentWorldMode)
      : null);
  };
  readBackstories();

  /**
   * The achievement record, for the STANDINGS journey list (R939, R951).
   *
   * It is asked for ONE time a session: the tab redraws four times a second
   * and a journey value is the truth of the last flush either way. Only a
   * meta session asks — a direct page has no route to ask — and the list
   * still stands without it, with the objectives and no values.
   *
   * The call names the SEASON SCOPE, because the service keys a season
   * total on the mode, the world and the season together and answers empty
   * totals without one. The hello carries the world and the season; the
   * mode arrives as a word on the join answer, so `entry.worldMode` holds
   * it. It is asked AFTER the hello for exactly that reason, and again
   * after a season roll, where the scope is a new one.
   */
  const askForTheRecord = (kind: SpawnKind): void => {
    if (!metaMode) return;
    const h = state.hello;
    if (!h) return;
    if (kind === 'season') resetRecord();
    const scope = { mode: currentWorldMode, world: h.worldId, season: h.season };
    askRecord(() => getMyAchievements(scope));
  };

  // The replay FILM (R531): the Replay frame a fetch answers with, played
  // on THIS engine with the map's own graphics. It borrows the four
  // dynamic slots, hides the sovereignty for its length and gives
  // everything back on close. It is the one replay a REPLAY press opens
  // (R542): the card of R528 retired on 2026-09-05.
  const film = new ReplayFilm({
    engine,
    camera,
    view: engine.view,
    live: { fleets, torpedoes, fx, wake, monsters: scene.monsters },
    root: ui.filmEl,
    shell: ui.shellEl,
    box: () => ({ w: canvas.clientWidth, h: canvas.clientHeight }),
    mapScale: () => state.tacticalScale,
    regenCalmMapSec: () => state.regenCalmMapSec,
    torpedoStyle: look.world.torpedo,
    // The Torpor's slow ring (R2783) off the hello catalog.
    slowPx: (kind) => slowRingPx(kind, state.torpedo(kind)?.splash ?? 0),
    // The detonation look the map draws, and the splash of each round off
    // the catalog (R2079).
    detonation: () => fx.detonationId,
    torpedo: (kind) => state.torpedo(kind) ?? packRound(kind),
    // Who is watching, for the film's damage figures: a replay names its
    // commanders by NAME and carries no player id (R538).
    playerName: () => state.hello?.playerName ?? '',
    // A dock-in still in flight would fight the chrome's travel.
    abortDockIn: () => dockin.abort(),
    // The film of a replay link is the whole visit: ✕ goes back to the
    // book the link came from (R1684).
    onExit: () => { if (filmLink) backToBook(filmLink.world); },
  });

  // -- the viewport report -------------------------------------------------

  const reportRect: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  let viewportTimer: ReturnType<typeof setTimeout> | null = null;
  function reportViewport(): void {
    if (viewportTimer) clearTimeout(viewportTimer);
    viewportTimer = setTimeout(() => {
      viewportTimer = null;
      engine.view.viewRect(reportRect, window.innerWidth, window.innerHeight);
      conn.sendViewport(reportRect.x0, reportRect.y0, reportRect.x1, reportRect.y1);
    }, VIEWPORT_DEBOUNCE_MS);
  }
  // Event-driven, never a timer: a hidden tab throttles timers and the
  // server would then be told a stale rect (§10.3).
  camera.onChange = reportViewport;

  // -- the feed ------------------------------------------------------------

  /** Guards the re-join a refused ticket or a season roll asks for: one at
   *  a time. */
  let rejoining = false;

  /**
   * How many placements one handover asks for before it gives up, and how
   * long it waits between two of them (R766).
   *
   * The window matters. A world files its season report on a spooled
   * goroutine, so for some seconds after the roll the meta server still
   * counts the old season and cannot honour the reservation the assignment
   * batch wrote. The meta server learns the new season on a heartbeat, and
   * a heartbeat is every fifteen seconds.
   *
   * So fifteen seconds of retries is exactly one beat period and carries no
   * margin at all: a client that dials one moment after a beat waits nearly
   * a whole period for the next one, and the spool's own delay comes on top
   * of that. Ten tries three seconds apart cover twenty-seven seconds, which
   * is a missed beat and the spool together. Then the client says so rather
   * than retrying for ever.
   */
  const HANDOVER_TRIES = 10;
  const HANDOVER_WAIT_MS = 3_000;

  /**
   * Dials the world a join answer names.
   *
   * The world may be another one, so the client rebuilds the scene rather
   * than patching it. `Feed.onHello` does exactly that: it applies the
   * hello's grid and terrain before any layer reads them, and it plays the
   * arrival of a season roll.
   */
  const dial = (j: Join): void => {
    const next = fromJoin(j);
    currentWorld = next.worldId;
    noteFaultContext({ world: currentWorld });
    currentWorldMode = next.worldMode;
    readBackstories();
    conn.resume(next.target);
  };

  /**
   * Asks the meta server where this player plays now, and dials it.
   *
   * It names **no world**. A named world goes through the meta server's
   * `pickNamedWorld`, which bypasses the reservation the assignment batch
   * wrote, and the whole handover is then a player put back where the roll
   * took them from.
   */
  const askForAPlacement = (attempt: number): void => {
    bus.local('season_handover');
    apiJoin({ mode: 'core' })
      .then((j) => {
        dial(j);
        rejoining = false;
      })
      .catch((err) => {
        if (attempt + 1 >= HANDOVER_TRIES) {
          rejoining = false;
          conn.fatal(messageOf(err));
          return;
        }
        setTimeout(() => askForAPlacement(attempt + 1), HANDOVER_WAIT_MS);
      });
  };

  /**
   * Takes the arrival off the glass. The black goes first: `#fatal` is inside
   * `#ui`, which is a stacking context of its own, so it cannot rise above
   * the cover (R182).
   */
  const dropArrival = (): void => {
    dockin.abort();
    banner.skip();
  };

  /**
   * Covers the screen because a maintenance window stands (deployment.md
   * §6.1). `ui/maintenance.ts` owns the rule; this page only has to take
   * the arrival off the glass first, because the dock-in and the season
   * banner both stand over the map and neither belongs on a dead world.
   */
  const coverForMaintenance = (certain: boolean) => coverIfMaintenance(certain, dropArrival);

  // The unlock cards (achievements.md §4, R936). It is built before the
  // feed, because the feed files an unlock into it, and it stands on the
  // shell so a card is inside the chrome's own stacking context.
  const unlocks = new UnlockQueue({
    host: ui.shellEl,
    notice: (text, o) => ui.notice(text, o),
    play: (id) => audio.play(id),
  });
  // The welcome card holds the first frame (R2127, player-reach.md §8.5).
  // A title card that rose before it goes and rises again at the close. A
  // grade the seat reaches while the card stands is the autopilot's, so it
  // takes no card and leaves one quiet line after the toasts.
  ui.firstHold.watch({
    hold: () => {
      unlocks.holdHandover();
      if (banner.showing) {
        banner.skip();
        ui.firstHold.defer('banner', RANK.title, heldBanner);
      }
    },
    release: () => { ui.firstHold.defer('handover', RANK.handover, () => unlocks.endHandover()); },
  });

  // The season summary card (R1518, ui-map-interface.md §3.4). It stands
  // in the chrome's own stacking context, and it opens by itself when the
  // hub sends the summary. MENU is the dock's own path back to the hub.
  // REGISTER of a guest the roll deletes (R1565): it leaves the way MENU
  // leaves, onto the hub's SETTINGS tab, where the ACCOUNT page links a
  // sign-in method. The guest card and the season summary share it.
  const registerGuest = (): void => {
    leaveWorldFor('register', hubAddress(location.href, 'settings'));
  };
  new SeasonSummaryCard({
    host: ui.shellEl,
    state,
    menu: () => actions.menu(),
    record: heldRecord,
    // A page with no meta server holds no record: the section says that
    // the record is not available instead of waiting (§3.4, R2089).
    recordFailed: () => !metaMode || recordFailed(),
    // The season timelapse at the foot of the card (R1620): the card asks
    // for the frames when it opens.
    queryTimelapse: () => actions.timelapseQuery(),
    titleHeld: () => ui.titleHeld(),
    // A guest the roll deletes reads the guest sentence in the place of
    // YOU CARRY, and REGISTER (R2089). Only a meta page has an account.
    sweptAtRoll: metaMode ? () => getMe().then((me) => {
      const h = state.hello;
      return h !== null && rollDeletes(guestFacts(me), {
        mode: currentWorldMode, seasonEndsSec: Number(h.seasonEndsUnix), timescale: state.timescale,
      });
    }) : undefined,
    register: registerGuest,
  });

  // The drain banner (R2094, ui-map-interface.md §3.4.1): the band on the
  // map while the drain of the settlement takes the board, with the clock
  // to the season summary card. It opens and goes by itself, off the
  // settlement clock of the wire.
  new SettlementBanner({ host: ui.shellEl, state });

  // The guest warning before the season roll (R1565, deployment.md §10.3).
  // It opens one time a season at seaquake stage 1 in a Conquest world, for
  // a guest the roll deletes. A direct page names no mode, so it never asks.
  // It shares the top slot with the explainer card of the top bar: it
  // closes that card when it rises, and yields while one stands (R2139).
  new GuestWarnCard({
    host: ui.shellEl,
    state,
    mode: () => currentWorldMode,
    getMe,
    register: registerGuest,
    barTip: ui,
  });

  // The dot of a hidden tab (R1653) reads the count of the INBOX cap, so
  // the INBOX count switch takes it away too (inbox.md §6, audit U1); a
  // repeat of the count draws nothing.
  if (tabIcon) state.on(() => tabIcon.setInbox(ui.inboxCount()));

  let advisorAsked = false;
  const feed = new Feed({
    scene,
    state,
    bus,
    unlocks,
    // The buzz of a telephone (R1612): the switches are the settings blob's,
    // read at each event, so a press in the hub reaches the next buzz.
    buzz: new Buzzer(browserBuzzEnv(), (id) => !ui.settings.buzzOff.includes(id)),
    // The live tab icon hears the siege warnings (R1653).
    tabIcon: tabIcon ?? undefined,
    // The card names the grades of the season that just locked, so the
    // record is asked again: the one the hello asked for is hours old.
    onSeasonSummary: () => askForTheRecord('season'),
    // The advisor speaks in the first session of a device (R2202): the
    // first Empire frame of the page decides once. The send is no press,
    // so it writes neither `ap.advisor.set` nor the account.
    onEmpire: (e) => {
      if (advisorAsked) return;
      advisorAsked = true;
      if (firstSessionAdvisor({
        first: isFirstSession(), meta: metaMode, on: e.advisorOn, set: advisorSetHere(),
      })) conn.sendEmpireSetting(ADVISOR_KEY, true);
    },
    heatmap,
    hexFills,
    hexBorders,
    minimap,
    commands: () => commands,
    tooltip: () => tooltip,
    openReplay: (r) => film.open(r),
    ui: () => ui,
    conn: () => conn,
    reportViewport,
    cues: watch,
    audio,
    onFatal: (reason: string, kind: FatalKind) => {
      // A dead connection takes the arrival off the glass with it.
      dropArrival();
      ui.fatal(reason, kind);
    },
    arrival: {
      // A login is covered from the line that built the dock-in, and a
      // resync covers nothing: that player is looking at this world already.
      cover: (kind) => { if (kind === 'season') dockin.cover(); },
      start: (kind) => {
        // A replay link keeps the world under the black until its film
        // opens (R1684). The cover starts again here, so its watchdog
        // counts from the hello and outlasts the link's wait; the film
        // takes the black off when it opens.
        if (filmLink && !film.isOpen()) {
          dockin.cover();
          return;
        }
        arrivalKind = kind;
        // The `#at=x,y` knob (R2492): BACK TO MAP of the sea view opens the
        // map at the place the eye was. It acts at the first arrival of the
        // page alone, after the hello centred the map on the hold; a
        // season roll and a resync keep the hold. The knob also marks the
        // return from the sea view, which plays no arrival: the player left
        // this world a moment ago, so the map stands at once with the
        // chrome docked (R2975).
        let seaReturn = false;
        if (!atKnobRead) {
          atKnobRead = true;
          const at = readAtKnob();
          if (at) {
            camera.centerOn(at.x, at.y);
            seaReturn = true;
          }
        }
        if (seaReturn) {
          dockin.abort();
        } else {
          const spawnScene = scene.spawnHost(() => {
            const z = engine.view.scale.x;
            spawnHome.x = scene.home.x * z + engine.view.x;
            spawnHome.y = scene.home.y * z + engine.view.y;
            return spawnHome;
          });
          spawn.start(look.world.spawn, kind, spawnScene);
          coveredArrival.started();
          // The arrival owns the screen from here, so the black can go. The
          // chrome stays outside the viewport until it closes (R182). A resync
          // is not covered, and this is a no-op for it.
          dockin.reveal();
        }
        // The preview knob (R938): `#unlock=<code>:<grade>,…` plays those
        // cards once, after the world is up. It changes nothing on the wire
        // and it is not a setting; the hash is the only way in.
        if (!unlockKnobPlayed) {
          unlockKnobPlayed = true;
          for (const u of readUnlockKnob()) unlocks.push(u);
        }
        // The journey values (R951). The hello is stored by now, so the
        // call can name the world, the mode and the season it belongs to.
        askForTheRecord(kind);
      },
    },
    onTicketRejected: !metaMode ? undefined : () => {
      // Close code 4003 means "get a new ticket" (deployment.md §3 step 4).
      if (rejoining) return;
      rejoining = true;
      bus.local('rejoining');
      apiJoin({ mode: 'core', world: currentWorld })
        .then(dial)
        .catch((err) => conn.fatal(messageOf(err)))
        .finally(() => { rejoining = false; });
    },
    // Close code 4002: the season rolled, this world gave the seat back,
    // and the meta server holds the placement the assignment batch wrote
    // (R766). A direct-mode client has no meta server to ask, so it keeps
    // no handler and the connection reconnects on its own backoff.
    onSeasonRolled: !metaMode ? undefined : () => {
      if (rejoining) return;
      rejoining = true;
      askForAPlacement(0);
    },
    // Close code 4004: a maintenance window opened and the world closed
    // every socket (deployment.md §6.1). There is nothing to reconnect
    // to — the game instances stop behind the gate — so the answer is the
    // cover, and the cover reloads the page when the gate comes down.
    onMaintenance: () => { void coverForMaintenance(true); },
    // Three plain failures in a row. The host may be behind the gate, and
    // a host behind the gate sends no close code at all, because the
    // process is already down. The probe decides; a negative answer covers
    // nothing and the backoff carries on.
    onMaintenanceSuspect: () => { void coverForMaintenance(false); },
  });

  // -- the pointer path ----------------------------------------------------

  /** CSS px to world px, without the shake. The point object is reused. */
  const toWorld = (cx: number, cy: number, out: { x: number; y: number }) =>
    engine.view.screenToWorld(cx, cy, out);
  const setCursor = (cursor: string) => { canvas.style.cursor = cursor; };
  /**
   * The HOVER's own cursor, resolved through the armed mode (R469, R500):
   * while a pick mode or the aim is armed the pointer goes away and the
   * drawn crosshair is the only cursor. The tooltip says what the pointer
   * is on; `Commands` says whether anything is armed.
   */
  const setMapCursor = (over: 'default' | 'pointer') => commands.setMapCursor(over);

  // The map markers (R1294, player-reach.md §2.2): the marker menu stands
  // in `#ui` over the canvas, and one input decides between the menu and
  // the remove for the alt+click and for the pinned peek alike.
  const markerMenu = new MarkerMenu({
    host: ui.shellEl,
    look: () => look.marks.mapMarker,
    audience: () => ui.settings.markerAudience,
    setAudience: (a) => {
      ui.settings.markerAudience = a;
      saveSettings(ui.settings);
    },
    place: (kind, cell, audience) => conn.sendMarkerPlace(kindWire(kind), cell, audienceWire(audience)),
    inks: MARKER_INKS,
    fell: CHART_FELL_FAMILY,
  });
  const markerInput = new MarkerInput({
    state,
    wire: () => conn,
    menu: markerMenu,
    toWorld,
    scale: () => engine.view.scale.x,
  });
  // A press on the peek's MARK or REMOVE (R1294, R2002).
  ui.onTooltipAction = () => tooltip.runPeekAction();
  // A press on the peek's SEARCH HERE or SEARCH (v2 §9, R2629, R2649).
  ui.onTooltipWork = () => tooltip.runPeekWork();
  // A ⚑ press in the peek (R2633): the peek goes before the report card.
  ui.onTooltipDismiss = () => tooltip.dismissAll();
  // A row of the cell list shows its object alone, and the press back
  // shows the list again (R2708); the pager turns its page (R2851).
  ui.onTooltipObject = (key) => tooltip.focusObject(key);
  ui.onTooltipPage = (page) => tooltip.pageObjects(page);
  // The small mark the tooltip picks: one object for every pick (R2851).
  const smallMark = newSmallMark();
  // The small marks (R2851): a wreck and a point of interest of the chart
  // furniture, then a pirate den (R2720), each in its drawn box grown by
  // `margin` world px. The nearest anchor wins. The tooltip and the point
  // pick of a press (R3138) each hold one mark object, so a pick
  // allocates nothing.
  const pickSmallMark = (x: number, y: number, margin: number, out: SmallMark): SmallMark | null => {
    clearSmallMark(out);
    scene.chart?.markPick(x, y, margin, out);
    // A den the layer hides at far zoom takes no pick (R2851).
    const den = scene.dens.visible ? denAt(state, x, y, margin) : null;
    if (den && offerSmallMark(out, 'den', cellCenterX(den.cell), cellCenterY(den.cell), x, y)) out.den = den;
    return out.kind ? out : null;
  };
  const pointMark = newSmallMark();
  // MARK on a contact card (R2002): the menu opens on the contact, for
  // the cell the contact stands on now.
  ui.onMarkContact = (id) => {
    const at = fleets.at(id);
    if (!at) return;
    const p = engine.view.worldToScreen(at.x, at.y, { x: 0, y: 0 });
    markerMenu.open(p.x, p.y, cellAt(at.x, at.y));
  };

  // The quick wheel (R1607, mockup round 077, card 01): the comma opens it
  // at the pointer, and a long press on the VIEW button opens it over the
  // middle of the map (R1610, R1747). It opens on the channel of the open
  // chat tab when that is SQUAD or FACTION.
  const quickChannelOf = (ch: ChatChannel): QuickChannel | null => {
    if (ch === ChatChannel.CHAT_CHANNEL_SQUAD) return 'squad';
    return ch === ChatChannel.CHAT_CHANNEL_FACTION ? 'faction' : null;
  };
  const quickWheel = new QuickWheel({
    host: ui.shellEl,
    ctx: () => quickCtxOf(state, fleets, Date.now() / 1000),
    look: posLookupOf(() => state),
    tabChannel: () => quickChannelOf(state.channel),
    hasSquad: () => state.clan !== null,
    send: (line, channel, pick) => conn.sendQuick(
      channel === 'squad' ? ChatChannel.CHAT_CHANNEL_SQUAD : ChatChannel.CHAT_CHANNEL_FACTION,
      line, pick.kind, pick.id),
    sheet: isSheet,
  });
  installViewHold(ui.mapButtonsElement, quickWheel, () => ui.toggleView());
  ui.onViewOpen = () => tooltip.dismissAll();
  // The QUICK press of the chat input (R1799).
  installQuickPress(ui.chatElement, quickWheel);

  tooltip = new Tooltip({
    ui: () => ui,
    state,
    fleets,
    sites: () => scene.sites,
    sonar,
    minimap,
    owners: () => scene.owners,
    blocked: () => scene.blocked,
    contestByCell: scene.contestByCell,
    gaugeByCell: scene.gaugeByCell,
    baseCells: scene.baseCells,
    structureCells: scene.structureCells,
    toWorld,
    toScreen: (x, y, out) => engine.view.worldToScreen(x, y, out),
    hitTest: (cx, cy) => commands.hitTest(cx, cy),
    setCursor: setMapCursor,
    // Over the rock of an own find with no name, the peek carries NAME,
    // which opens the naming sheet (R2633); else MARK or REMOVE.
    peekAction: (cx, cy) => {
      const at = toWorld(cx, cy, { x: 0, y: 0 });
      const ridge = scene.chart?.ridgeAt(cellAt(at.x, at.y)) ?? null;
      return namePress(ridge, (body) => ui.openSeamountFind(body)) ?? markerInput.peek(cx, cy);
    },
    // The ridge peek carries the elevation of the ridge (round 086 card
    // 06 b): the chart furniture holds the ridges and their names.
    ridge: (cell) => scene.chart?.ridgeAt(cell) ?? null,
    historical: () => scene.chart?.reading === 'historical',
    // The small marks (R2851), grown by the margin of a finger.
    mark: (x, y, marginPx) => pickSmallMark(x, y, marginPx / engine.view.scale.x, smallMark),
    // A long press on a shipping lane of the faction peeks its card
    // (R2658); the sea objects of the cell take the press first.
    markPeek: (cx, cy) => {
      const lane = laneOfMark(commands.markAt(cx, cy)?.id ?? '');
      return lane ? ui.lanePeek(lane) : null;
    },
    // SEARCH HERE on the water, or at the point of the wreck that the card
    // shows when it holds cargo (R2860), while an own fleet is selected
    // and `treasure` holds (R2629, R2649); SEARCH and its hull picker
    // while none is (R2699). The peek names the wreck it shows (R2851).
    peekWork: (x, y, wreck) => {
      const cell = cellAt(x, y);
      const sel = commands.ownSelection();
      return workPress(state, {
        fleetId: sel?.id ?? 0,
        // Only a combat ship or a Dredger works (v2 §9.6, R2834).
        fit: sel !== undefined && workFit(state, sel.cls),
        cell,
        rock: cell >= 0 && scene.blocked?.[cell] === BLOCK_RIDGE,
        wreck,
        search: (id, c) => commands.searchAt(id, c),
        searchPoint: (id, x, y) => commands.searchPoint(id, x, y),
        // With no own fleet selected, SEARCH opens the picker (R2699), at
        // the point of a wreck with cargo (R2860).
        pick: (c, at) => ui.openSearchPicker(c, at),
      });
    },
    // The heat cell peek reads the field the map paints (round 086 card
    // 12, R1825). Every peek of a map hover outlines its cell, dashed, in
    // the slot of its own (R1875).
    heat: () => heatmap,
    cellRing: (cell) => hoverCell.setCell(cell),
    // The sea objects of the picture, with the life of an own sea mine
    // counted down (R2657): the water peek shows their cards.
    seaObjects: () => state.seaObjects.views(Date.now()),
  });

  // The on-ramp offer and the lit course line (R3075, ui-map-interface.md
  // §8 "The on-ramp offer", §6.1): the water, the lanes and the lights of
  // the picture, and the offers of the own fleets after a MOVE.
  const laneWaters = new LaneWaters(state);
  const laneOffers = new LaneOfferDesk({
    waters: laneWaters,
    fleet: (id) => {
      const h = fleets.helmOf(id);
      const f = h ? fleets.orderOf(id) : null;
      if (!h || !f) return null;
      const uh = state.ship(h.cls)?.speedUh ?? 0;
      return { x: f.x, y: f.y, mission: h.mission, tx: f.tx, ty: f.ty, pxPerMapSec: (h.deep ? uh / 2 : uh) / 3600 };
    },
    move: Mission.MISSION_MOVE,
    lifeMs: FLEET_COURSE.offerSec * 1000,
    graceMs: FLEET_COURSE.graceSec * 1000,
    now: () => performance.now(),
  });
  // The lit runs of a standing course, kept on its wire list and the
  // revision of the water, so a pack walks no water (§10).
  const courseLitMemo = new WeakMap<object, { rev: number; runs: number[][] }>();
  fleets.courseLit = (legs) => {
    const rev = laneWaters.rev;
    const hit = courseLitMemo.get(legs);
    if (hit && hit.rev === rev) return hit.runs;
    const pts = legs.map((w) => ({ x: mpxToPx(w.xMpx), y: mpxToPx(w.yMpx) }));
    const runs: number[][] = [];
    for (let k = 1; k < pts.length; k++) runs.push(laneWaters.runs(pts[k - 1], [pts[k]]));
    courseLitMemo.set(legs, { rev, runs });
    fleets.courseLitInk = factionColor(state.faction);
    return runs;
  };
  plotLayer.lit = (from, pts) => ({ runs: laneWaters.runs(from, pts), ink: factionColor(state.faction) });

  commands = new Commands({
    ui: () => ui,
    conn: () => conn,
    state,
    fleets,
    sites: () => scene.sites,
    camera,
    tooltip,
    toWorld,
    scale: () => engine.view.scale.x,
    setCursor,
    // A press is offered to the chart furniture first (R548): a mark is
    // drawn over the water it stands on. The battle marks of card 52 are
    // what answers, and the press opens the film of the fight it names.
    chart: () => scene.chart,
    // The pennants of the commissions take a press too (R2988).
    commissions: () => state.empire?.commissions ?? [],
    // A press on the mark of a point of interest selects the point
    // (R3138); a nearer wreck or den keeps the press for the peek.
    pointAt: (wx, wy, marginPx) => {
      const m = pickSmallMark(wx, wy, marginPx / engine.view.scale.x, pointMark);
      return m?.kind === 'poi' && m.poi && poiHasCard(state, m.poi) ? m.poi.id : 0;
    },
    // The vents of the survey are gather fields, as the deposits are: the
    // GATHER pick and the context order of a Dredger take them (R3147).
    pois: () => state.survey.pois,
    // The press IS the reading (R694). The film opens first, then the
    // message is marked read: the fresh Inbox frame takes the mark's words
    // and leaves its darts, and the unread badge stops counting a fight the
    // commander just watched. Two `sendInbox` messages, because
    // `hub.inboxAction` answers one intent per message. The piece carries
    // `film = 'hide'`, so the rebuild under the open film shows no flicker.
    onMark: (id) => {
      // A commission pennant opens the commission card (R2988).
      const com = commissionOfMark(id);
      if (com > 0) { ui.openCommissionCard(com); return; }
      // A shipping lane of the faction opens its lane card (R2658).
      const lane = laneOfMark(id);
      if (lane) { ui.openLaneCard(lane); return; }
      // An inbox pin opens its NEEDS YOU item as a message page (R1846).
      const pinId = pinMessageId(id);
      if (pinId > 0) { ui.openInboxMessage(pinId); return; }
      const msgId = markMessageId(id);
      const msg = state.inbox.find((m) => m.id === msgId);
      if (!msg?.replay) return;
      actions.openReplay(msg.replay);
      if (!state.inboxIsRead(msg)) {
        actions.inboxRead(msgId, false);
        state.markInboxRead([msgId]);
      }
    },
    // The order that names a place acknowledges itself (R547).
    local: (id) => bus.local(id),
    // A round selection lets its answer speak (R3121).
    roundOrdered: (id) => watch?.noteRoundOrder(id),
    // The on-ramp offer of a transit MOVE (R3075).
    laneOffer: laneOffers,
  });

  // A link that plays no film goes back to the book too, and the hub
  // states why (R1684).
  if (filmLink) {
    armReplayLink(feed.handlers, filmLink, {
      send: (ref) => conn.sendInbox({ fetchReplay: ref }),
      silent: () => backToBook(filmLink.world, 'silent'),
      gone: () => backToBook(filmLink.world, 'gone'),
      waitMs: CHROME_REPLAY_LINK_WAIT_MS,
    });
  }
  // A press on a push notification: the address of a new window names
  // the cell, and a message reaches a tab that is already in the world.
  if (cellLink && metaMode && cellLink.world === entry.worldId) {
    armCellLink(feed.handlers, cellLink.cell, (cell) => actions.focusCell(cell));
  }
  listenPushOpen(() => currentWorld, (cell) => actions.focusCell(cell));
  conn = new Connection(entry.target, feed.handlers);

  // -- the overlay's read-back hooks --------------------------------------

  // WAR · SOVEREIGNTY reads the beams the state frames carry (R116).
  ui.beams = () => scene.beams;
  // Whose things wear the squad marks (R199).
  ui.squadmate = (accountId) => accountId !== 0 && scene.squadIds.has(accountId);
  // The marks the squad chip and its key draw (R1571): the look the map
  // draws, and the hold symbol in the reading the map draws it in.
  ui.squadLook = () => ({ scene: sceneLook, holdSymbol: scene.holdSymbol, era: scene.markEra });
  // The FLEETS dock and the selection panel read the fleet layer.
  ui.fleets = () => fleets.own();
  ui.contact = (id) => fleets.describe(id);
  // The siege window reads every hull the layer holds (ui/siegewindow.ts).
  ui.presence = () => fleets.presence();
  // The contact card states the aspect while the chevrons show (R2457).
  ui.chevronsShown = (id) => fleets.chevronsShown(id);
  // The monster card states the design and the act (R2719).
  ui.monsterOf = (id) => scene.monsters.read(id);
  // The helm keys of the map (R2707): one STEER record a fleet, and the
  // numbers of its order on the card.
  const mapHelm = new MapHelm((id, mrad, step) => conn.sendSteer(id, mrad, step));
  const helmFleet = (id: number): HelmFleet | null => {
    const h = fleets.helmOf(id);
    if (!h) return null;
    const ship = state.ship(h.cls);
    const uh = ship?.speedUh ?? 0;
    return {
      id, headingRad: h.headingRad, speedPxS: h.speedPxS, mission: h.mission,
      topPxS: ((h.deep ? uh / 2 : uh) * state.tacticalScale) / 3600,
      turnRadS: turnRadS(ship?.turnRateDegS ?? 0, state.tacticalScale),
    };
  };
  ui.helmOf = (id) => mapHelm.read(id);
  // The on-ramp offer on the card, and VIA LANE (R3075).
  ui.laneOfferOf = (id) => {
    const o = laneOffers.of(id);
    return o ? { lane: o.lane, saveMapSec: o.saveMapSec } : null;
  };
  ui.onLaneOffer = (id) => { if (commands.takeLaneOffer(id)) ui.render(true); };
  // RALLY (the game critique of 2026-10-14): a squad marker on the object
  // unless an own live marker stands there, and the H-hour quick line to
  // SQUAD with the object. A mate's client buzzes on that line (ui/buzz.ts).
  ui.rally = (siteId, cell, defend) => {
    const now = Date.now() / 1000;
    const marked = state.mapMarkers.some((m) => m.ownerId === state.playerId && m.cell === cell
      && (m.expiresUnix === 0 || m.expiresUnix > now));
    if (!marked) conn.sendMarkerPlace(kindWire(defend ? 'defend' : 'attack'), cell, audienceWire('squad'));
    conn.sendQuick(ChatChannel.CHAT_CHANNEL_SQUAD, quickLineOf('siege'), QuickRefKind.QUICK_REF_KIND_OBJECT, siteId);
  };

  // The gun ring of the selected hull (R386). The layer mirrors no rule
  // table, so it reads the reach of a hull class out of the hello catalog.
  // A planted tower draws its own ring, because a set anchor moves the
  // reach (R661); `gunRange` holds that one rule for both modes.
  // DIG IN moves the reach too (R2394), by the figure of the catalog rules.
  fleets.gunRangeOf = (cls, anchor, active) => gunRange(anchor, state.ship(cls),
    active, state.rules.approach?.digInReachPx ?? 0);
  // The dead zone disc of the selected hull (R1472), out of the same catalog.
  fleets.deadZoneOf = (cls) => state.ship(cls)?.minRange ?? 0;
  // The rear dead zone (R2391): the gun arc of the class, out of the same
  // catalog, under the all-round gates.
  fleets.gunArcOf = (cls, anchor, active, close) => rearArcDeg(state.ship(cls), anchor, active, close);
  // The base aim ring (R386). `home` is the base position, so the layer
  // needs one more figure: the reach of the round the base has armed.
  // `ui.aiming === 0` is the base, and `null` is nobody.
  fleets.baseAimRange = () => (ui.aiming === 0
    ? state.torpedo(ui.baseTorp)?.range ?? 0
    : 0);

  // The five hidden view toggles (R456). The keyboard flips the fields for
  // the session; the World theme sets where they start (themes.md §9):
  // `default` is the shipped picture, `hex` starts with the hex fills and
  // borders on and the sovereignty off. The `hex` knob defaults to 1, so a
  // `hex` theme shows the hex views with no knob in the address.
  const layerView = startLayerView(look);
  // The Chart theme's word over the piece table (themes.md §3.6). AUTO is
  // null, which is the pack's own gate exactly.
  scene.chart?.setOverlay(chartOverlay(look));
  ui.chartPieces = chartOverlay(look);
  const summonRect: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  // Photo mode (R1623, ui-map-interface.md §13.1): OPTIONS · PHOTO or
  // shift with p. The veil hides the chrome, the camera runs past its
  // range, the hex lines answer the GRID switch, and the fleet layer drops
  // the selection marks until photo mode ends.
  const photo = new PhotoMode({
    shell: ui.shellEl,
    camera,
    grid: { get: () => layerView.hexBorders, set: (on) => { layerView.hexBorders = on; } },
    veil,
    capture: () => captureMap(engine),
    png: pngOf,
    facts: () => ({
      world: state.hello?.worldName ?? '',
      season: state.hello?.season ?? 0,
      day: seasonDay(state),
      clockSec: state.worldClockSec(),
    }),
    font: () => getComputedStyle(ui.shellEl).fontFamily || 'monospace',
    onEnter: () => {
      commands.cancelModes();
      tooltip.leave();
      fleets.selectedId = 0;
    },
    onLeave: () => {
      const sel = state.selection;
      fleets.selectedId = sel?.kind === 'fleet' ? sel.id : 0;
    },
  });
  ui.onPhoto = () => photo.enter();
  // DONE of the plot line sends the course (v2 §7, R2589). The map press
  // that adds a leg is `Commands.plotLeg`, which the click, the tap and
  // the right click ask first.
  ui.onPlotDone = (fleetId, legs) => commands.course(fleetId, legs);
  // The client log holds no other player's chat (R1627): the world chat
  // and the clan chat of this page name what it must drop.
  clientLog()?.setChatSource(() => otherPlayersChat(
    state.chat, state.selfAccountId,
    state.clanChat.entries, state.clanChat.selfId));
  // The REPORT sheet (R1626, fault-reports.md §4.4): OPTIONS · REPORT. It
  // needs the meta server, so a direct lane has no row. A report that met
  // a lost link waits in the outbox and goes out when the link is back.
  if (metaMode) {
    const store = storeOf(); // no storage: no gap and no outbox on disk
    const outbox = new ReportOutbox({ send: sendPlayerReport, store, notice: (line) => ui.notice(line) });
    outbox.start();
    const sheet = new ReportSheet({
      shell: ui.shellEl,
      facts: () => {
        const sel = state.selection;
        const f = sel?.kind === 'fleet' ? fleets.describe(sel.id) : null;
        return {
          worldId: state.hello?.worldId ?? 0,
          world: state.hello?.worldName ?? '',
          tick: feed.lastTick,
          day: seasonDay(state),
          clockSec: state.worldClockSec(),
          fleets: f ? [{ id: f.id, name: f.name }] : [],
          renderer: engine.gfx.backend,
          page: pageOf(location.pathname),
          build: pageBuild(),
          browser: browserOf(navigator.userAgent),
        };
      },
      account: () => getMe().then((me) => ({ name: me.account.name, guest: me.account.kind === 'guest' })),
      log: () => clientLog()?.tail() ?? { text: '', lines: 0, bytes: 0 },
      shot: () => reportShot(engine),
      send: sendPlayerReport,
      outbox,
      store,
      now: () => Date.now(),
      also: () => [document.getElementById('fast-minimap')],
      copy: copyText,
    });
    ui.onReportProblem = () => sheet.open();
    // The welcome card steps behind the sheet while it stands (R1637).
    ui.reportOpen = () => sheet.isOpen;
  }
  const dragView = installInput({
    canvas, ui: () => ui, state, camera, commands, tooltip, fleets, minimap,
    layerView,
    // The `.` key (§6.4, a debugging key): a beast rises now (R525), in the
    // view the owner is looking at, in a trench there when there is one (R545).
    summon: () => kraken.summon(
      undefined, engine.view.viewRect(summonRect, window.innerWidth, window.innerHeight)),
    // A replay film covers the map and drives its own three keys (R531).
    suspended: () => film.isOpen(),
    // The season summary veils the chrome, and its keys with it (R1519).
    veiled: () => veil.active,
    // Photo mode takes no order, and a tap shows or hides its bar (R1623).
    photo: { on: () => photo.on, tap: () => photo.tap(), toggle: () => photo.toggle() },
    // `alt` with a click puts a map marker, or removes an own one (R1294).
    markers: markerInput,
    // A held comma opens the quick wheel at the pointer (R1611).
    quick: quickWheel,
    // What the touch log states of a press (R1498); only a log line reads it.
    probe: { view: engine.view, fleets: () => fleets, lodMin: CAMERA_DETAIL_MIN_ZOOM },
    // The arrows steer the selected own fleet at sea (R2707, R2830).
    // A turn key turns the rudder while it is held, as in the helm view (R3237).
    helm: {
      key: (k, repeat) => {
        const sel = state.selection;
        if (sel?.kind !== 'fleet') return false;
        const f = helmFleet(sel.id);
        if (!f) return false;
        mapHelm.press(f, k, performance.now(), repeat);
        return true;
      },
      lift: (k) => mapHelm.lift(k),
    },
  });

  // -- the window ----------------------------------------------------------

  // The clamp is written against the window, so a resize or a rotation can
  // strand the view outside it. Clamping fires `onChange`, which is what
  // reports the new viewport. The drawing surface itself is resized by the
  // context's own `ResizeObserver`.
  window.addEventListener('resize', () => camera.reclamp());

  // The loop starts and stops itself on the page's visibility (R190). This
  // listener adds the other half: a hidden page is never told the fingers
  // lifted, so the camera would count one that is gone.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) camera.reset();
  });

  // -- the per-frame chrome ------------------------------------------------

  /** What the link banner last showed, so the cue fires on the edge only. */
  let linkUp = false;
  let linkSeen = false;
  // R131: the per-frame buffers the loop must not rebuild sixty times a
  // second. Before the first hello there is no site layer, and a shared
  // empty array keeps the chart's identity compare quiet.
  const NO_STRUCTURES: MinimapSite[] = [];
  /** The home marker moves only when a hello replaces the world. */
  let homeHello: unknown = null;
  let homeMark: { x: number; y: number } | null = null;
  /**
   * The settlement (seaquake stage 3) switches the sonar off. The layer
   * caches its coverage circles, so they are cleared once on the way in.
   */
  let wasSettling = false;
  /**
   * The crosshair's own memory (R469, R500). The mode is compared so a
   * change reaches the cursor once; the snap probe is hit-test work, so it
   * runs when its answer can have changed and not once a frame (§10.3).
   * Every object here is reused: a frame allocates nothing (R131).
   */
  let lastCrosshairMode: CrosshairMode = null;
  let lastPlotOn = false;
  /** The heat field the peek last read under a still pointer (R1825). */
  let lastHeatField: Uint8Array | null = null;
  /** The camera of the last frame, for the peek's leader (R1875). */
  const lastPeekView = { x: 0, y: 0, s: 0 };
  const lastSnapAt = { x: -1, y: -1 };
  let lastSnapMode: CrosshairMode = null;
  let lastSnapScale = 0;
  let lastSnapped = false;
  /** One reused world point for the crosshair's placement. */
  const crossPt = { x: 0, y: 0 };
  /**
   * The drag preview's own memory (R1208). The decision is hit-test work,
   * so it runs when the finger moved, the zoom changed or the refresh is
   * due, and not once a frame (§10.3). The decision object is reused.
   */
  const dragMission = newContextMission();
  const lastDragAt = { x: -1, y: -1 };
  /** The lane course of the on-ramp offer under the drag (R3075), and the
   *  hull, the point and the water revision it was walked for, so a still
   *  finger walks no lane again. */
  let dragLane: DragLane | null = null;
  const dragLaneAt = { fx: NaN, fy: NaN, tx: NaN, ty: NaN, rev: -1 };
  const dragLaneOf = (fx: number, fy: number, tx: number, ty: number): DragLane | null => {
    const rev = laneWaters.rev;
    const k = dragLaneAt;
    if (k.fx === fx && k.fy === fy && k.tx === tx && k.ty === ty && k.rev === rev) return dragLane;
    k.fx = fx; k.fy = fy; k.tx = tx; k.ty = ty; k.rev = rev;
    const from = { x: fx, y: fy }, to = { x: tx, y: ty };
    const c = laneWaters.course(from, to);
    const runs = laneWaters.runs(from, [to]);
    if (c) runs.push(...laneWaters.runs(from, c.legs));
    dragLane = c || runs.length > 0 ? { legs: c?.legs ?? [], runs, ink: factionColor(state.faction) } : null;
    return dragLane;
  };
  let lastDragScale = 0;
  let lastDragMs = 0;
  /** What the scene shows, rewritten every frame and never rebuilt (R131). */
  const vis: SceneVisibility = {
    fluff: true, knobs, detail: true, sonar: false,
    chart: 'off', holdSymbol: look.marks.hold, filming: false,
  };

  /**
   * The chrome's share of a frame: the link line and its two events, the
   * pinned peek and photo mode. Every frame runs it, and so does the idle
   * sweep of a map the cover state paused (R1755), once a second, so a
   * lost link is heard under the telephone menu too.
   */
  chromeFrame = (): void => {
    // A dropped link has to show up now, not at the next 1 Hz sweep (R108).
    ui.setLink(conn.connected, conn.retryAt);
    if (conn.connected !== linkUp) {
      linkUp = conn.connected;
      // The first connect of the session is an arrival and stays silent;
      // every later transition is news.
      if (linkSeen) bus.local(linkUp ? 'link_up' : 'link_down');
      linkSeen = true;
    }
    // A pinned peek has no pointer holding it up, so its own clock is the
    // only thing that can take it away. One number compare a frame.
    tooltip.tickPeek();
    // The zoom reading of photo mode; one boolean test while it is off.
    photo.sync();
  };

  onFrame = (frame) => {
    chromeFrame?.();

    // The armed pick, as a crosshair (R469, R500). It is read here and not
    // pushed from each site that arms a mode: the overlay's own action
    // handler writes `ui.hunting` directly, so a push would miss every
    // button. The layer skips a frame that changed nothing, so a still
    // pointer costs one comparison.
    const scale = engine.view.scale.x;
    const cmode = commands.crosshairFor();
    // The plot line wears the plotter cursor (R2705), so its start and its
    // end reach the cursor as a mode change does.
    const plotOn = ui.plotting();
    if (cmode !== lastCrosshairMode || plotOn !== lastPlotOn) {
      lastCrosshairMode = cmode;
      lastPlotOn = plotOn;
      // The mode decides the cursor, so a change has to reach it. The hover
      // path owns both the cursor and the tooltip, so re-running it keeps
      // the two in step; with no pointer on the canvas the cursor is all
      // there is to set.
      if (tooltip.hover) tooltip.refreshAtCursor();
      else setMapCursor('default');
    }
    const aim = commands.aimPoint;
    const hover = tooltip.hover;
    if (cmode === null) crosshair.clear();
    else if (cmode === 'aim' && aim) {
      // A finger cannot hover, so a mission pick draws nothing on touch and
      // the hint host at the context slot carries the mode (R2164). The aim is different: the first tap
      // places the solution, so the mark draws there and the player sees
      // what the second tap will fire.
      toWorld(aim.cx, aim.cy, crossPt);
      crosshair.set('aim', crossPt.x, crossPt.y, false);
    } else if (hover) {
      if (hover.x !== lastSnapAt.x || hover.y !== lastSnapAt.y
        || cmode !== lastSnapMode || scale !== lastSnapScale) {
        lastSnapAt.x = hover.x;
        lastSnapAt.y = hover.y;
        lastSnapMode = cmode;
        lastSnapScale = scale;
        lastSnapped = commands.snapAt(cmode, hover.x, hover.y);
      }
      toWorld(hover.x, hover.y, crossPt);
      crosshair.set(cmode, crossPt.x, crossPt.y, lastSnapped);
    } else crosshair.clear();

    // The drag order preview (R1208). The hull and its target both move
    // under a still finger, so the refresh re-reads them on its own clock.
    if (dragView.dragging) {
      if (dragView.x !== lastDragAt.x || dragView.y !== lastDragAt.y
        || scale !== lastDragScale || frame.now - lastDragMs >= FLEET_ORDER_DRAG.refreshMs) {
        lastDragAt.x = dragView.x;
        lastDragAt.y = dragView.y;
        lastDragScale = scale;
        lastDragMs = frame.now;
        const from = fleets.at(dragView.fleetId);
        const m = from ? commands.contextMission(dragView.x, dragView.y, dragMission) : null;
        if (from && m) {
          orderDrag.set(from.x, from.y, m.wx, m.wy, m.kind, m.snapped);
          // The on-ramp offer under a transit MOVE alone (R3075).
          orderDrag.setLane(m.kind === 'move' ? dragLaneOf(from.x, from.y, m.wx, m.wy) : null);
        } else orderDrag.clear();
      }
    } else {
      lastDragAt.x = -1;
      orderDrag.clear();
    }
    // The draft course (R2589): one reference compare a frame. The set
    // course of the fleet it plots for steps aside (R2704).
    const draft = ui.plotDraft();
    plotLayer.set(draft);
    fleets.plotFleet = draft?.fleetId ?? 0;
    // The STEER records of the helm keys: a waiting change, the refresh
    // inside the lease, and the let go (R2707).
    mapHelm.tick(performance.now(), helmFleet);

    // The display switches (R124, ☸ OPTIONS). All three are presentation
    // only. A switch that is off hides the layer AND skips its per-frame
    // work (§10.3) — an invisible layer is neither advanced nor drawn.
    const opts = ui.settings;
    const fluffVisible = opts.fluff;
    // The mark scale of the season map (R1992, ui-map-interface.md §6.1.9):
    // the switch `markScaleCore` of the blob. Off is 1, so the season
    // picture of a player who never turns it on does not move a pixel; a
    // set is one field write on each layer.
    const markK = markFactor('core', opts);
    fleets.setMarkScale(markK);
    torpedoes.setMarkScale(markK);
    // Zoom LOD (§10.17, R456, R501). Under this scale a fleet mark is a few
    // pixels of noise, so the map drops the four tactical layers AND their
    // per-frame work — the sole point of the gate is the work, not the
    // pixels. There is no hysteresis: nothing here rebuilds a texture, so a
    // reader who parks the wheel on the threshold pays a few flag writes. A
    // `detail_min_zoom` of 0 turns the gate off and every layer draws at
    // every zoom (R492).
    const detail = scale >= CAMERA_DETAIL_MIN_ZOOM;
    // The fluff: the OPTIONS switch is the umbrella, and one knob per piece
    // sits under it (R499, R504). The seaquake layer is NOT in the scene's
    // list — it stays visible so its clock runs, and the silt rides `feed`
    // below.
    const k = knobs;
    // The seaquake's settlement (stage 3) takes the sonar with it: a
    // coverage ring drawn over a ruin is the one thing left on screen
    // claiming the world still works.
    const stage = state.board?.quakeStage ?? 0;
    const settling = stage >= 3;
    // A replay film has the glass (R531). It is read here, above the
    // visibility block, because three writes below answer it and the chart
    // furniture must learn it on EVERY frame — a film takes the scene's own
    // frame away, and half of the furniture stays on the picture under one.
    const filming = film.isOpen();
    scene.chart?.setFilming(filming);
    // A film shows no siege of today and stamps no grade over the fight
    // (R2404). Both act on an edge only.
    scene.sites?.setFilming(filming);
    unlocks.setHeld(filming);
    // The chart furniture's reading (R548): the stored player setting. The
    // hold symbol rides beside it (R633) and takes its reading from it, so
    // both are read here, every frame, like every other display switch.
    const chartPick = opts.chart;
    // What the scene shows this frame, and what it must therefore advance.
    // The scene ANDs the LOD gate onto the sonar and the wake itself.
    vis.fluff = fluffVisible;
    vis.knobs = k;
    // While the WAR scrubber shows a season timelapse frame, the hulls
    // are hidden: the map states the owners of the frame and nothing of
    // the live sea (R1620). The LOD gate already hides every hull layer
    // and skips its work, so the frame rides it.
    vis.detail = detail && !ui.warTl.showing;
    vis.sonar = opts.sonar && !settling;
    vis.chart = chartPick;
    vis.chartHide = opts.chartHide;
    vis.filming = filming;
    if (settling && !wasSettling) sonar.reset();
    wasSettling = settling;
    // The seaquake (R101, the settlement entries): the stage and the drain
    // progress of the settlement clock drive the tremor; the fluff switch
    // gates the silt only. The card phase is calm water: the drain is over
    // and no shock lands (R2092).
    const drainSec = state.rules.settlementDrainSec;
    const settleT = state.drainEndsMs !== 0 && drainSec > 0
      ? 1 - state.drainLeftSec() / drainSec : null;
    quake.feed(state.settlementCard() ? 0 : stage, settleT, fluffVisible);
    // The five hidden view keys (R456, R501). They are written here, every
    // frame, rather than in the key handler: a hello can replace what a
    // layer holds, and the picture must not depend on when a key was
    // pressed. The two sovereignty terms are skipped passes of the
    // territory layer: it skips the wash pass or the glow pass.
    territory.setFills(layerView.sovFills);
    territory.setBorders(layerView.sovBorders);
    terrain.setVisible(layerView.terrain);
    // The three writes below are the ones a film would fight: it hides both
    // hex views and the chart travels out with the rest of the chrome, and
    // a visibility written every frame would put them straight back.
    // The two hex views answer the key AND the `hex` knob (R504).
    hexFills.setVisible(!filming && layerView.hexFills && k.hex === 1);
    hexBorders.setVisible(!filming && layerView.hexBorders && k.hex === 1);
    minimap.setOpen(!filming && opts.minimap && !veil.hidden);
    // The chrome dock-in (R182). The panels are CSS transitions and cost
    // nothing here; the chart is a canvas of its own and cannot be one, so it
    // follows the same eased clock from this line. All three calls are no-ops
    // for all but the one second an arrival lasts.
    dockin.tick();
    minimap.setDockProgress(dockin.progress);
    // The flash and the ring are the dock-in's own, because which of the
    // two a value plays is a figure of that value and not of the chart
    // (R826). A value with neither hands over two zeros.
    minimap.setDockFlash(dockin.flash);
    minimap.setDockRing(dockin.ring);

    if (state.hello !== homeHello) {
      homeHello = state.hello;
      homeMark = state.hello ? { x: scene.home.x, y: scene.home.y } : null;
      // A hello replaces the terrain, so the whale scatter and the silt's
      // ridges are read again from it. The beast's trenches are the
      // scene's, and `applyHello` has already handed them over (R525).
      creatures.setBlocked(scene.blocked);
      quake.setBlocked(scene.blocked);
    }
    // The round minimap shows the heat field while a mode paints (R1801).
    // Three identity compares a frame; a new field redraws the disc once.
    minimap.setHeat(heatmap.mode, heatmap.field, scene.blocked);
    // A new heat field under a still pointer reads the cell again (R1825):
    // one identity compare a frame.
    if (heatmap.field !== lastHeatField) {
      lastHeatField = heatmap.field;
      if (tooltip.hover) tooltip.refreshAtCursor();
    }
    // The peek's leader ends at a mark or at the centre of the hovered
    // cell, so a camera that moves under a still pointer moves the leader
    // too (R1875). Three compares a frame; the peek writes its DOM only
    // when its markup changed (§10.3).
    if (tooltip.hover && (engine.view.x !== lastPeekView.x || engine.view.y !== lastPeekView.y
      || scale !== lastPeekView.s)) {
      tooltip.refreshAtCursor();
    }
    lastPeekView.x = engine.view.x;
    lastPeekView.y = engine.view.y;
    lastPeekView.s = scale;
    // The telephone scale bar beside the round unit (R1801): it reads the
    // zoom and the chart reading, and writes only on a change.
    ui.scaleBar.update(
      scale, chartPick === 'historical',
      isSheet() && chartPick !== 'off' && !filming && clusterBox('rose').visible, uiZoom(),
    );
    minimap.tick(scene.sites?.structures() ?? NO_STRUCTURES, frame.view, homeMark);

    // The map's own frame (R503): the visibility writes, the ear, one fleet
    // snapshot, the sonar rings, the sonar, the ping-lit snow and the wake.
    //
    // While a film runs, the FILM's feed takes its place (R531). The
    // scene's frame rewrites the visibility of the fleets, the rounds, the
    // wake, the sonar and the fluff on every frame, so it would take the
    // film's four slots back the moment it ran.
    if (filming) film.frame(frame);
    else scene.frame(frame, vis);
    // The damage figures' motion (R538), from the World theme. The look is
    // fixed for the session; the write stays in the frame because a film
    // shares the layer, and `setStyle` is one field write.
    scene.damage.setStyle(look.world.damage);

    // Three sources push the camera: the seaquake's tremor, the arrival's
    // own shudder (Seaquake Settle asks for one) and a torpedo style's
    // impact jolt. They ride ONE `setShake` call (R164), and screen shake
    // off zeroes the feed rather than pausing it. The scene's share is the
    // jolt, and it zeroes itself with the round layer.
    // A film hides the seaquake, so its tremor clock is standing still and
    // a live offset off a frozen layer would hold the view at a tilt for
    // the whole film (R531).
    const shake = opts.screenshake && !filming;
    camera.setShake(
      shake ? quake.shakeX + spawn.shakeX + scene.shakeX : 0,
      shake ? quake.shakeY + spawn.shakeY + scene.shakeY : 0,
    );
    // The arrival's zoom nudge (R838). Rise from the Deep is the one source,
    // and the layer resets the three figures on its own finish, so this is a
    // copy and never a decision.
    engine.view.nudge.k = spawn.nudgeK;
    engine.view.nudge.ax = spawn.nudgeAx;
    engine.view.nudge.ay = spawn.nudgeAy;
  };

  statsSink = makeStatsSink({
    // The slow sweep carries the sound as well as the figures. Volumes and
    // playlist picks are cheap to re-apply and nothing here needs a frame's
    // precision; the media card's clock reads in whole seconds.
    onSweep: () => { audio.applySettings(soundSettings(ui.settings, look)); pushAudio(); },
    ui: () => ui,
    conn: () => conn,
    gfx: () => engine.gfx,
    state,
    tick: () => feed.lastTick,
    contacts: () => fleets.count,
    zoom: () => engine.view.scale.x,
  });

  // -- the measurement -----------------------------------------------------

  if (PERF_ON) {
    perf = installPerf({
      camera: {
        centerOn: (x, y, zoom) => camera.centerOn(x, y, zoom),
        scale: () => engine.view.scale.x,
      },
      world: () => ({ w: WORLD, h: WORLD_H }),
      home: () => scene.home,
      owners: () => (state.hello ? scene.owners : null),
      population: () => state.hello?.population ?? 0,
      fleets: () => {
        // The same rect the frame computes, read once per path rather than
        // held in a variable the shipping frame would have to write.
        engine.view.viewRect(reportRect, window.innerWidth, window.innerHeight);
        const { x0, y0, x1, y1 } = reportRect;
        let inView = 0;
        for (const f of fleets.info()) {
          if (f.x >= x0 && f.x <= x1 && f.y >= y0 && f.y <= y1) inView++;
        }
        return { total: fleets.count, inView };
      },
      backend: backendLabel(engine.gfx),
      // The frame loop already holds the timer-query ring's last result.
      gpuMs: () => engine.loop.stats.gpuMs,
      counters: () => ({
        drawCalls: engine.loop.stats.drawCalls,
        instances: engine.loop.stats.instances,
        contacts: fleets.count,
        sites: scene.sites?.count ?? 0,
      }),
    });
    // The matrix runs on its own five seconds after the first hello: the
    // layers are built by then and the first frames have settled.
    const waitForHello = setInterval(() => {
      if (!state.hello) return;
      clearInterval(waitForHello);
      setTimeout(() => {
        // An operator who typed `__perf.run(…)` in the console first owns
        // the run, and this one is refused. That refusal is not a fault:
        // say so and let theirs finish.
        void perf?.run('standard').then(publishReport)
          .catch((err) => console.info('fast: the automatic perf run stood down —', err));
      }, PERF_SETTLE_MS);
    }, 250);
  }

  // -- go ------------------------------------------------------------------

  engine.start();

  // The same handle the shipping client publishes, plus what is new here.
  // An operator reads the layers through it and the CDP harness drives it.
  const debug = {
    engine, camera, conn, state, scene, feed, ui, commands, tooltip, minimap,
    fleets, control, sonar, torpedoes, fx, wake,
    hexFills, hexBorders, markers, crosshair,
    layerView,
    spawn, dockin, banner,
    fog, snow, haze, creatures, kraken, quake,
    chart: scene.chart,
    knobs: () => knobs,
    audio, film,
    clanChat: () => clanChat,
    sites: () => scene.sites,
  };
  (window as unknown as { __debug: typeof debug }).__debug = debug;
  const handle = (window as unknown as { __fast?: { camera: unknown } }).__fast;
  if (handle) handle.camera = camera;
}

void boot();
